import React, { useState, useEffect, useRef } from "react";
import ClaudeService from "../services/claudeService";
import TypewriterText from "./TypewriterText";

// ═══════════════════════════════════════════════════════════════
// COUCH PANEL — Reset your nervous system
// Six modes: Watch, Breathe, Meditate, Play, Satisfy, Music
// Lumen soft mode at bottom. No tasks. No nudging.
// ═══════════════════════════════════════════════════════════════

const TEAL = "#4DB8B5";
const DARK = "#1A1A2E";
const MODES = [
  { id: "watch",    icon: "▶",  label: "Watch"    },
  { id: "breathe",  icon: "◎",  label: "Breathe"  },
  { id: "meditate", icon: "✦",  label: "Meditate" },
  { id: "play",     icon: "◈",  label: "Play"     },
  { id: "satisfy",  icon: "◉",  label: "Satisfy"  },
  { id: "music",    icon: "♪",  label: "Music"    },
];

// ── Watch mode ─────────────────────────────────────────────────
const STREAMING = [
  { id: "youtube",  label: "YouTube", color: "#FF0000", icon: "▶" },
  { id: "netflix",  label: "Netflix", color: "#E50914", icon: "N" },
  { id: "hulu",     label: "Hulu",    color: "#1CE783", icon: "h" },
  { id: "hbo",      label: "Max",     color: "#002BE7", icon: "M" },
];

function WatchMode() {
  const [activeService, setActiveService] = useState("youtube");
  return (
    <div className="flex flex-col h-full gap-3">
      {/* Service selector */}
      <div className="flex gap-2 flex-shrink-0">
        {STREAMING.map(s => (
          <button key={s.id} onClick={() => setActiveService(s.id)}
            className="flex-1 py-2 rounded-[10px] border-none cursor-pointer text-[13px] font-bold font-['DM_Sans'] transition-all duration-200"
            style={{ background: activeService===s.id ? s.color : "rgba(255,255,255,0.08)", color: activeService===s.id ? "white" : "rgba(255,255,255,0.5)" }}>
            {s.label}
          </button>
        ))}
        <button className="w-10 rounded-[10px] border border-dashed border-white/20 bg-transparent text-white/40 text-lg cursor-pointer">
          +
        </button>
      </div>

      {/* Video embed */}
      <div className="flex-1 rounded-xl overflow-hidden bg-black relative min-h-0">
        {activeService === "youtube" ? (
          <iframe
            width="100%" height="100%"
            src="https://www.youtube.com/embed/fYlE4P7esPo?autoplay=0&rel=0&modestbranding=1"
            title="Calming video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <div className="text-[40px] opacity-30">
              {STREAMING.find(s=>s.id===activeService)?.icon}
            </div>
            <div className="text-[13px] text-white/40 font-['DM_Sans'] text-center leading-[1.6]">
              Connect your {STREAMING.find(s=>s.id===activeService)?.label} account<br/>
              <span className="text-[11px] opacity-60">Coming soon</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Breathe mode ───────────────────────────────────────────────
function BreatheMode() {
  const PHASES = [
    { label: "Breathe in",  duration: 4, color: "#4DB8B5" },
    { label: "Hold",        duration: 4, color: "#9B8FE0" },
    { label: "Breathe out", duration: 6, color: "#4DB8B5" },
    { label: "Hold",        duration: 2, color: "#9B8FE0" },
  ];
  const [running, setRunning]     = useState(false);
  const [phase,   setPhase]       = useState(0);
  const [elapsed, setElapsed]     = useState(0);
  const [cycles,  setCycles]      = useState(0);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setElapsed(e => {
        const next = e + 0.05;
        if (next >= PHASES[phase].duration) {
          setPhase(p => {
            const np = (p + 1) % PHASES.length;
            if (np === 0) setCycles(c => c + 1);
            return np;
          });
          return 0;
        }
        return next;
      });
    }, 50);
    return () => clearInterval(t);
  }, [running, phase]);

  const current = PHASES[phase];
  const progress = elapsed / current.duration;
  const circleSize = 120 + progress * 80;
  const TOTAL = 4;

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6">
      {/* Breathing circle */}
      <div className="relative w-[280px] h-[280px] flex items-center justify-center">
        {/* Outer pulse ring */}
        <div className="absolute rounded-full transition-all duration-[50ms] linear"
          style={{ width: circleSize*1.4, height: circleSize*1.4, background: `rgba(${current.color==="#4DB8B5"?"77,184,181":"155,143,224"},0.08)` }} />
        {/* Main circle */}
        <div className="absolute rounded-full flex items-center justify-center flex-col gap-1 transition-all duration-[50ms] linear"
          style={{
            width: circleSize,
            height: circleSize,
            background: `radial-gradient(circle, rgba(${current.color==="#4DB8B5"?"77,184,181":"155,143,224"},0.25) 0%, rgba(${current.color==="#4DB8B5"?"77,184,181":"155,143,224"},0.08) 100%)`,
            border: `2px solid rgba(${current.color==="#4DB8B5"?"77,184,181":"155,143,224"},0.4)`
          }}>
          <div className="text-base font-semibold text-white/90 font-['DM_Sans']">{current.label}</div>
          <div className="text-[28px] font-bold text-white font-['DM_Sans']">
            {Math.ceil(current.duration - elapsed)}
          </div>
        </div>
      </div>

      {/* Cycle counter */}
      <div className="text-xs text-white/40 font-['DM_Sans']">
        {running ? `${cycles} cycle${cycles!==1?"s":""} completed` : "4-4-6-2 box breathing"}
      </div>

      {/* Controls */}
      <button onClick={() => { setRunning(r=>!r); if(!running){setPhase(0);setElapsed(0);} }}
        className="py-3 px-9 rounded-[30px] border-none cursor-pointer text-sm font-semibold font-['DM_Sans'] transition-all duration-200"
        style={{ background: running ? "rgba(255,255,255,0.1)" : TEAL, color: running ? "rgba(255,255,255,0.7)" : "white" }}>
        {running ? "Pause" : "Start"}
      </button>
    </div>
  );
}

// ── Meditate mode ──────────────────────────────────────────────
const MEDITATIONS = [
  { id: "body",    label: "Body scan",       duration: 120, color: "#9B8FE0",
    script: ["Close your eyes and settle in.", "Notice where your body meets the surface beneath you.", "Scan slowly from your feet upward.", "Nowhere to be. Nothing to do.", "Just here."] },
  { id: "breath",  label: "Breath awareness", duration: 120, color: "#4DB8B5",
    script: ["Breathe naturally. Don't control it.", "Just notice each inhale.", "Notice each exhale.", "Thoughts will come. Let them pass.", "Return to the breath."] },
  { id: "reset",   label: "Quick reset",      duration: 60,  color: "#FFB830",
    script: ["Take one deep breath.", "Let your shoulders drop.", "Release the jaw.", "You're okay.", "Back whenever you're ready."] },
];

function MeditateMode() {
  const [selected, setSelected]   = useState(null);
  const [running,  setRunning]    = useState(false);
  const [elapsed,  setElapsed]    = useState(0);
  const [lineIdx,  setLineIdx]    = useState(0);

  useEffect(() => {
    if (!running || !selected) return;
    const t = setInterval(() => {
      setElapsed(e => {
        const next = e + 1;
        if (next >= selected.duration) { setRunning(false); return selected.duration; }
        setLineIdx(Math.floor((next / selected.duration) * selected.script.length));
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, selected]);

  const start = (m) => { setSelected(m); setRunning(true); setElapsed(0); setLineIdx(0); };
  const stop  = () => { setRunning(false); setSelected(null); setElapsed(0); };
  const pct   = selected ? elapsed / selected.duration : 0;
  const mins  = selected ? Math.floor((selected.duration - elapsed) / 60) : 0;
  const secs  = selected ? (selected.duration - elapsed) % 60 : 0;

  if (running && selected) return (
    <div className="flex flex-col items-center justify-center h-full gap-8">
      {/* Progress ring */}
      <div className="relative w-[180px] h-[180px]">
        <svg width="180" height="180" className="absolute inset-0">
          <circle cx="90" cy="90" r="80" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4"/>
          <circle cx="90" cy="90" r="80" fill="none" stroke={selected.color} strokeWidth="4"
            strokeDasharray={`${502 * pct} 502`} strokeLinecap="round"
            transform="rotate(-90 90 90)" style={{ transition:"stroke-dasharray 1s linear" }}/>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          <div className="text-[28px] font-bold text-white font-['DM_Sans']">
            {mins}:{secs.toString().padStart(2,"0")}
          </div>
        </div>
      </div>
      {/* Current line */}
      <div className="text-lg text-white/85 font-['DM_Sans'] text-center leading-[1.6] max-w-[280px] animate-[fadeIn_0.8s_ease-out]" key={lineIdx}>
        {selected.script[lineIdx] || "..."}
      </div>
      <button onClick={stop} className="py-2.5 px-7 rounded-[20px] border border-white/15 bg-transparent text-white/50 text-[13px] cursor-pointer font-['DM_Sans']">End session</button>
    </div>
  );

  return (
    <div className="flex flex-col h-full gap-3 py-2">
      <div className="text-[13px] text-white/40 font-['DM_Sans'] text-center mb-1">
        Pick a session. Close your eyes. Let go.
      </div>
      {MEDITATIONS.map(m => (
        <button key={m.id} onClick={() => start(m)}
          className="py-[18px] px-5 rounded-[14px] border border-white/[0.08] bg-white/[0.04] cursor-pointer text-left flex items-center gap-3.5 transition-all duration-150 font-['DM_Sans']"
          onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.08)"}
          onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,0.04)"}>
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: m.color, boxShadow: `0 0 8px ${m.color}` }} />
          <div>
            <div className="text-sm font-semibold text-white/85 mb-0.5">{m.label}</div>
            <div className="text-[11px] text-white/[0.35]">{m.duration/60} min</div>
          </div>
          <div className="ml-auto text-lg text-white/20">▶</div>
        </button>
      ))}
    </div>
  );
}

// ── Play mode ──────────────────────────────────────────────────
const GAMES = [
  { id: "2048",   label: "2048",         desc: "Slide tiles, clear your head", color: "#FFB830", url: "https://play2048.co/" },
  { id: "snake",  label: "Snake",        desc: "Classic. Mindless. Perfect.",  color: "#4DB8B5", url: "https://playsnake.org/" },
  { id: "tetris", label: "Tetris",       desc: "Flow state in 30 seconds",     color: "#9B8FE0", url: "https://tetris.com/play-tetris" },
  { id: "lines",  label: "Color Lines",  desc: "Calming pattern matching",     color: "#FF7A40", url: "https://colorlines.com/" },
];

function PlayMode() {
  const [active, setActive] = useState(null);
  if (active) return (
    <div className="flex flex-col h-full gap-2">
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={() => setActive(null)}
          className="py-1.5 px-3 rounded-lg border border-white/15 bg-transparent text-white/50 text-xs cursor-pointer font-['DM_Sans']">← Back</button>
        <span className="text-[13px] text-white/40 font-['DM_Sans']">{active.label}</span>
      </div>
      <div className="flex-1 rounded-xl overflow-hidden bg-black min-h-0">
        <iframe src={active.url} width="100%" height="100%"
          className="border-none block" title={active.label}/>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full gap-2.5 py-2">
      <div className="text-[13px] text-white/40 font-['DM_Sans'] text-center mb-1">
        Easy. Mindless. That's the point.
      </div>
      <div className="grid grid-cols-2 gap-2.5 flex-1">
        {GAMES.map(g => (
          <button key={g.id} onClick={() => setActive(g)}
            className="py-5 px-4 rounded-[14px] border border-white/[0.07] bg-white/[0.04] cursor-pointer text-left flex flex-col gap-2 transition-all duration-150 font-['DM_Sans']"
            onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.09)"}
            onMouseLeave={e => e.currentTarget.style.background="rgba(255,255,255,0.04)"}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-extrabold text-white"
              style={{ background: g.color, boxShadow: `0 4px 12px ${g.color}55` }}>
              {g.label[0]}
            </div>
            <div>
              <div className="text-sm font-semibold text-white/85 mb-[3px]">{g.label}</div>
              <div className="text-[11px] text-white/[0.35] leading-[1.4]">{g.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Satisfy mode ───────────────────────────────────────────────
const SATISFY_VIDEOS = [
  { label: "Sand cutting",    id: "wGO9lMOrLRk" },
  { label: "Kinetic sand",    id: "h2YSCseeJeU" },
  { label: "Soap cutting",    id: "l5sBKJGBONM" },
  { label: "Slime",           id: "7gQpYNdcpAk" },
];

function SatisfyMode() {
  const [active, setActive] = useState(SATISFY_VIDEOS[0]);
  return (
    <div className="flex flex-col h-full gap-2.5">
      <div className="flex-1 rounded-xl overflow-hidden bg-black relative min-h-0">
        <iframe width="100%" height="100%"
          src={`https://www.youtube.com/embed/${active.id}?autoplay=0&rel=0&modestbranding=1`}
          title={active.label} frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"/>
      </div>
      <div className="flex gap-2 flex-shrink-0 flex-wrap">
        {SATISFY_VIDEOS.map(v => (
          <button key={v.id} onClick={() => setActive(v)}
            className="py-1.5 px-3.5 rounded-[20px] border-none cursor-pointer text-xs font-['DM_Sans'] transition-all duration-200"
            style={{ background: active.id===v.id ? TEAL : "rgba(255,255,255,0.08)", color: active.id===v.id ? "white" : "rgba(255,255,255,0.5)" }}>
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Music mode ─────────────────────────────────────────────────
const MUSIC_CHANNELS = [
  { label: "Lo-fi",       id: "jfKfPfyJRdk", color: "#9B8FE0" },
  { label: "Nature",      id: "eKFTSSKCzWA", color: "#4DB8B5" },
  { label: "Focus",       id: "7NOSDKb0HlU", color: "#FFB830" },
  { label: "Deep sleep",  id: "1ZYbU82GVz4", color: "#4A6FA5" },
];

function MusicMode() {
  const [active, setActive] = useState(MUSIC_CHANNELS[0]);
  return (
    <div className="flex flex-col h-full gap-3">
      {/* Channel selector */}
      <div className="flex gap-2 flex-shrink-0 flex-wrap">
        {MUSIC_CHANNELS.map(c => (
          <button key={c.id} onClick={() => setActive(c)}
            className="flex-1 min-w-[80px] py-2 rounded-[10px] border-none cursor-pointer text-xs font-semibold font-['DM_Sans'] transition-all duration-200"
            style={{
              background: active.id===c.id ? c.color : "rgba(255,255,255,0.08)",
              color: active.id===c.id ? "white" : "rgba(255,255,255,0.5)",
              boxShadow: active.id===c.id ? `0 4px 14px ${c.color}55` : "none"
            }}>
            {c.label}
          </button>
        ))}
      </div>
      {/* Now playing */}
      <div className="flex-1 rounded-xl overflow-hidden bg-black relative min-h-0">
        <iframe width="100%" height="100%"
          src={`https://www.youtube.com/embed/${active.id}?autoplay=0&rel=0&modestbranding=1`}
          title={active.label} frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"/>
      </div>
    </div>
  );
}

// ── Lumen soft mode ────────────────────────────────────────────
function LumenSoft() {
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([
    { role:"lumen", text:"Hey. No agenda here. How are you actually doing?" }
  ]);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const messagesContainerRef = useRef(null);
  const prevMessagesLenRef = useRef(-1);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const isFirstRun = prevMessagesLenRef.current === -1;
    const grew = messages.length > prevMessagesLenRef.current;
    prevMessagesLenRef.current = messages.length;
    if (isFirstRun) {
      container.scrollTop = container.scrollHeight;
    } else if (grew) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(m => [...m, { role:"user", text:userMsg }]);
    setLoading(true);
    try {
      const result = await ClaudeService.sendMessage({
        model:"claude-sonnet-4-6",
        maxTokens:200,
        system:`You are Lumen in soft/couch mode inside NeuroQuest. The user is taking a break. Your role here is purely supportive - warm, present, no agenda. No productivity. No tasks. Just be with them. Short responses. Ask how they're feeling. Validate. 2-3 sentences max.`,
        messages: messages.concat({ role:"user", content:userMsg })
          .map(m => ({ role: m.role==="lumen"?"assistant":"user", content:m.text }))
      });

      if (result.success) {
        setMessages(m => [...m, { role:"lumen", text: result.content || "I'm here." }]);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Couch mode error:", error);
      setMessages(m => [...m, { role:"lumen", text:"I'm here with you." }]);
    }
    setLoading(false);
  };

  if (!open) return (
    <div className="flex-shrink-0 px-4 py-2.5 border-t border-white/[0.06] flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-[#4DB8B5] flex items-center justify-center text-[10px] text-white font-bold">✦</div>
        <span className="text-xs text-white/40 font-['DM_Sans'] italic">
          Lumen is here if you want to talk
        </span>
      </div>
      <button onClick={() => setOpen(true)}
        className="py-[5px] px-3.5 rounded-[20px] border border-[#4DB8B5]/30 bg-[#4DB8B5]/10 text-[#4DB8B5] text-[11px] cursor-pointer font-['DM_Sans']">
        Open
      </button>
    </div>
  );

  return (
    <div className="flex-shrink-0 border-t border-white/[0.06] flex flex-col h-[220px]">
      {/* Lumen header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.05]">
        <div className="flex items-center gap-2">
          <div className="w-[22px] h-[22px] rounded-full bg-[#4DB8B5] flex items-center justify-center text-[9px] text-white font-bold">✦</div>
          <span className="text-xs font-semibold text-white/70 font-['DM_Sans']">Lumen</span>
        </div>
        <button onClick={() => setOpen(false)}
          className="bg-none border-none text-white/30 cursor-pointer text-sm">−</button>
      </div>
      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-auto px-4 py-2.5 flex flex-col gap-2">
        {messages.map((m, i) => (
          <div key={i} className="flex" style={{ justifyContent: m.role==="user"?"flex-end":"flex-start" }}>
            <div className="max-w-[80%] py-2 px-3 rounded-xl text-xs text-white/80 font-['DM_Sans'] leading-[1.5]"
              style={{ background: m.role==="lumen" ? "rgba(255,255,255,0.06)" : `rgba(77,184,181,0.2)` }}>
              <TypewriterText text={m.text} animate={i === messages.length - 1 && m.role === "lumen"} />
            </div>
          </div>
        ))}
        {loading && (
          <div className="text-xs text-white/30 font-['DM_Sans'] italic">Lumen is thinking...</div>
        )}
      </div>
      {/* Input */}
      <div className="flex gap-2 px-4 py-2 border-t border-white/[0.05]">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key==="Enter" && send()}
          placeholder="Just talk..."
          className="flex-1 bg-white/[0.06] border border-white/10 rounded-[20px] py-[7px] px-3.5 text-xs text-white/80 font-['DM_Sans'] outline-none"/>
        <button onClick={send} disabled={!input.trim() || loading}
          className="py-[7px] px-4 rounded-[20px] border-none cursor-pointer text-xs font-['DM_Sans'] transition-all duration-200"
          style={{ background: input.trim() ? TEAL : "rgba(255,255,255,0.08)", color: input.trim() ? "white" : "rgba(255,255,255,0.3)" }}>
          Send
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COUCH PANEL
// ═══════════════════════════════════════════════════════════════
export default function CouchPanel({ onClose, onMinimize }) {
  const [mode, setMode] = useState("watch");
  const [pipLabel, setPipLabel] = useState("Couch");

  const renderMode = () => {
    switch(mode) {
      case "watch":    return <WatchMode/>;
      case "breathe":  return <BreatheMode/>;
      case "meditate": return <MeditateMode/>;
      case "play":     return <PlayMode/>;
      case "satisfy":  return <SatisfyMode/>;
      case "music":    return <MusicMode onChannelChange={setPipLabel}/>;
      default:         return <WatchMode/>;
    }
  };

  const MODE_LABELS = {
    watch: "🎬 Watching", breathe: "◎ Breathing", meditate: "✦ Meditating",
    play: "◈ Playing", satisfy: "◉ Satisfying", music: `♪ ${pipLabel}`
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden rounded-2xl relative font-['DM_Sans']" style={{ background: DARK }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"/>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0 border-b border-white/[0.06]">
        <div className="flex items-center gap-2.5">
          <span className="text-[15px]">🛋</span>
          <span className="text-sm font-bold text-white/85">Couch</span>
          <span className="text-[11px] text-white/30">Reset your nervous system</span>
        </div>
        <div className="flex gap-1.5">
          {/* Minimize to PiP */}
          {onMinimize && (
            <button onClick={() => onMinimize(MODE_LABELS[mode])}
              title="Minimize - keep playing"
              className="bg-white/[0.06] border-none text-white/50 rounded-lg py-[5px] px-2.5 cursor-pointer text-[13px] font-['DM_Sans']">⊡</button>
          )}
          <button onClick={onClose}
            className="bg-white/[0.06] border-none text-white/40 rounded-lg py-[5px] px-3 cursor-pointer text-xs font-['DM_Sans']">✕</button>
        </div>
      </div>

      {/* Mode tabs */}
      <div className="flex px-4 pt-2.5 gap-1 flex-shrink-0">
        {MODES.map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            className="flex-1 py-2 rounded-t-lg border-none cursor-pointer font-['DM_Sans'] transition-all duration-150 flex flex-col items-center gap-[3px]"
            style={{
              background: mode===m.id ? "rgba(255,255,255,0.08)" : "transparent",
              borderBottom: mode===m.id ? `2px solid ${TEAL}` : "2px solid transparent",
              color: mode===m.id ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.35)",
              fontSize: 11,
              fontWeight: mode===m.id ? 600 : 400
            }}>
            <span className="text-sm">{m.icon}</span>
            <span>{m.label}</span>
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="flex-1 px-4 pt-3.5 overflow-hidden flex flex-col min-h-0">
        {renderMode()}
      </div>

      {/* Lumen soft mode */}
      <LumenSoft/>

      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ── Floating PiP widget (exported, lives in App.js layer) ───────
export function CouchPiP({ label, onExpand, onClose }) {
  const [pos, setPos] = useState({ x: window.innerWidth - 220, y: window.innerHeight - 100 });
  const dragging = useRef(false);
  const offset   = useRef({ x: 0, y: 0 });
  const didDrag  = useRef(false);

  useEffect(() => {
    const mv = e => {
      if (!dragging.current) return;
      didDrag.current = true;
      setPos({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y });
    };
    const up = () => { dragging.current = false; };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
  }, []);

  const onDown = e => {
    if (e.target.closest("button")) return;
    dragging.current = true; didDrag.current = false;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };

  return (
    <div onMouseDown={onDown}
      className="fixed z-[300] flex items-center gap-2 px-3 py-2 rounded-[28px] border border-white/12 shadow-[0_4px_20px_rgba(0,0,0,0.4)] cursor-grab select-none font-['DM_Sans'] animate-[pipIn_0.2s_ease-out]"
      style={{ left: pos.x, top: pos.y, background: "rgba(26,26,46,0.95)", backdropFilter: "blur(12px)" }}>
      {/* Pulse dot */}
      <div className="w-[7px] h-[7px] rounded-full flex-shrink-0 animate-[pulse_2s_infinite]" style={{ background: TEAL }}/>
      {/* Label */}
      <div onClick={() => { if (!didDrag.current) onExpand(); }}
        className="text-xs font-semibold text-white/85 cursor-pointer whitespace-nowrap max-w-[120px] overflow-hidden text-ellipsis">
        {label}
      </div>
      {/* Expand */}
      <button onClick={onExpand}
        className="bg-white/[0.08] border-none text-white/60 rounded-md w-[22px] h-[22px] cursor-pointer text-[10px] flex items-center justify-center">
        ⤢
      </button>
      {/* Close */}
      <button onClick={onClose}
        className="bg-none border-none text-white/[0.35] cursor-pointer text-xs px-0.5 leading-none">✕</button>
      <style>{`
        @keyframes pipIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>
    </div>
  );
}
